import map from "lodash/fp/map";
import multiply from "lodash/fp/multiply";
import random from "lodash/fp/random";
import split from "lodash/fp/split";
import findIndex from "lodash/fp/findIndex";
import zipObject from "lodash/fp/zipObject";
import zipWith from "lodash/fp/zipWith";

import stationInfoDialog from "../../components/dialogs/stationInfoDialog/stationInfoDialog.html";
import stationInfoDialogCtrl from "../../components/dialogs/stationInfoDialog/stationInfoDialog.controller";
import routeInfoDialog from "../../components/dialogs/routeInfoDialog/routeInfoDialog.html";
import routeInfoDialogCtrl from "../../components/dialogs/routeInfoDialog/routeInfoDialog.controller";

class Controller {
  constructor(
    $injector,
    $stateParams,
    $mdSidenav,
    $mdMenu,
    $mdDialog,
    $state,
    $q
  ) {
    "ngInject";

    this.$mdMenu = $mdMenu;
    this.$mdDialog = $mdDialog;
    this.$mdSidenav = $mdSidenav;
    this.$stateParams = $stateParams;
    this.$state = $state;
    this.$q = $q;
    this.Route = $injector.get("Route");
    this.Events = $injector.get("Events");
    this.Rougena = $injector.get("Rougena");

    this.editParamKeys = ["routeId"];
    this.paramKeys = ["polygon1", "polygon2", "time", "type"];
    this.paramValues = split(",", this.$stateParams.districtsQuery);

    this.isFetchingDemand = false;
    this.optimizationAllowed = true;

    this.updateStationsEvent = this.Events.getUpdateEvent("updateStations");
    this.updateDemandEvent = this.Events.getUpdateEvent("updateDemand");
    this.editStationEvent = this.Events.getUpdateEvent("editStation");
    this.toggleOptimizationEvent = this.Events.getUpdateEvent(
      "toggleOptimization"
    );
    this.toggleCreateModeEvent = this.Events.getUpdateEvent("toggleCreateMode");
    this.showNotificationEvent = this.Events.eventsType.onShowSuccessNotification;
  }

  $onInit() {
    const zipParams = (keys, values) => zipObject(keys, values);
    const errorEvent = this.Events.eventsType.onShowErrorNotification;
    if (this.paramValues && this.paramValues.length > 1) {
      this.params = zipParams(this.paramKeys, this.paramValues);
    } else {
      this.params = zipParams(this.editParamKeys, this.paramValues);
    }

    const getConfirmDialog = (title, details) =>
      this.$mdDialog
        .confirm()
        .title(title)
        .textContent(details)
        .ok("Ok")
        .cancel("Cancel");

    this.$mdSidenav("route-planning-sidenav").open();
    this.openMenu = ($mdMenu, ev) => {
      $mdMenu.open(ev);
    };

    const findId = (item) => item._id || item.id;

    const getRandomId = () => multiply(random(0, 5000000), random(0, 5000000));

    const getStationIdx = (station) => {
      const findById = (item) => item._id === station._id;
      return findIndex(findById, this.route.stations);
    };

    const formatRoute = (route, stations) => {
      return {
        _id: this.params.routeId || null,
        origin: route.polygon1,
        destination: route.polygon2,
        name: route.name_i18n
          ? route.name_i18n.en
          : `${route.polygon1.name} - ${route.polygon2.name}`,
        nameAr: route.name_i18n ? route.name_i18n.ar : route.nameAr || "",
        price: route.price || 0,
        description: route.description || "",
        city: route.polygon1.city,
        weight: route.weight || 0,
        time: route.time || null,
        type: route.type || null,
        hotLocations: route.hotLocations || null,
        stations,
      };
    };

    const formatStations = (station) => {
      const _id = station._id || getRandomId();
      const location = [station.lat, station.lng];
      const locationTxt = `${station.lat}, ${station.lng}`;
      const name = station.name_i18n
        ? station.name_i18n.en
        : station.name || `station - ${_id}`;
      const nameAr = station.name_i18n ? station.name_i18n.ar : "";
      const address = station.address_i18n ? station.address_i18n.en : "";
      const addressAr = station.address_i18n ? station.address_i18n.ar : "";

      return {
        ...station,
        _id,
        name,
        nameAr,
        address,
        addressAr,
        location,
        locationTxt,
        loc: {
          coordinates: [station.lng, station.lat],
        },
      };
    };

    const mapStations = (stations) =>
      map((station) => ({
        name_i18n: {
          ar: station.nameAr,
          en: station.name,
        },
        address_i18n: {
          en: station.address,
          ar: station.addressAr,
        },
        districtId: station.district ? station.district._id : null,
        lat: station.lat,
        lng: station.lng,
      }))(stations);

    const get = () => {
      const method =
        this.params && this.params.routeId ? "getRoute" : "suggestRoute";
      this.Rougena[method](this.params).$promise.then((response) => {
        const stations = response.stations || response.route;
        this.stations = map(formatStations)(stations);
        this.route = formatRoute(response, this.stations);
      });
    };

    get();

    const updateDemand = (demands) => {
      const { weight, stations } = demands;
      const zipper = (a, b) => ({ ...a, ...b });
      const updatedStations = zipWith(zipper, this.route.stations, stations);

      this.route.weight = weight;
      this.route.stations = updatedStations;
      this.stations = map(formatStations)(this.route.stations);
      this.Events.emit(this.updateStationsEvent, this.route.stations);
    };

    this.toggleOptimization = (status) => {
      if (status === true) {
        this.Events.emit(this.showNotificationEvent, {
          message:
            "Auto optimization should be disabled to apply manual sorting",
        });
      } else {
        this.Events.emit(this.showNotificationEvent, {
          message: "Manual Sorting Enabled",
        });
      }
      this.optimizationAllowed = status;
    };

    this.toggleCreateMode = (status) => {
      if (status === true) {
        this.Events.emit(this.showNotificationEvent, {
          message: "You can now place markers on map.",
        });
      }
    };

    this.enableSortStations = () => {
      this.sortableConfig.disabled = false;
      this.isSortStationsEnabled = true;

      this.originalStations = [...this.stations];
    };

    this.dismissSortStations = () => {
      this.sortableConfig.disabled = true;
      this.isSortStationsEnabled = false;
      this.isStationsSorted = false;

      this.stations = this.originalStations;
    };

    this.saveStationsSort = () => {
      this.isStationsSorted = false;
      this.route.stations = this.stations;
      this.Events.emit(this.updateStationsEvent, this.stations);
      this.isSortStationsEnabled = false;
    };

    this.onSortCallback = () => {
      this.isStationsSorted = true;
    };

    this.sortableConfig = {
      animation: 350,
      chosenClass: "sortable-chosen",
      ghostClass: "sortable-ghost",
      handle: ".grab-handle",
      forceFallback: true,
      disabled: true,
      onEnd: this.onSortCallback,
    };

    this.removeStation = (station) => {
      const stationIdx = getStationIdx(station);

      const removeStation = getConfirmDialog(
        "Remove Station",
        `Are you sure you want to remove station " ${this.route.stations[stationIdx].name} "`
      );

      this.$mdDialog.show(removeStation).then(
        () => {
          if (stationIdx >= 0) this.route.stations.splice(stationIdx, 1);
          this.stations = map(formatStations)(this.route.stations);
          this.Events.emit(this.updateStationsEvent, this.route.stations);
          this.getSatisfiedDemand();
        },
        () => {}
      );
    };

    this.editStation = (station) => {
      const stationIdx = getStationIdx(station);

      if (stationIdx >= 0) this.route.stations[stationIdx] = station;
      this.stations = map(formatStations)(this.route.stations);
      this.Events.emit(this.updateStationsEvent, this.route.stations);
      this.getSatisfiedDemand();
    };

    this.updateStationInfo = (station) => {
      const updateStation = this.$mdDialog.show({
        controller: stationInfoDialogCtrl,
        controllerAs: "$ctrl",
        template: stationInfoDialog,
        multiple: true,
        locals: {
          station,
        },
      });

      updateStation.then(
        (updatedStation) => {
          const station = {
            ...updatedStation,
            name: updatedStation.name.value,
            nameAr: updatedStation.nameAr.value,
            name_i18n: {
              en: updatedStation.name.value,
              ar: updatedStation.nameAr.value,
            },
            address: updatedStation.address.value,
            addressAr: updatedStation.addressAr.value,
            address_i18n: {
              en: updatedStation.address.value,
              ar: updatedStation.addressAr.value,
            },
            location: updatedStation.location.value,
            district: updatedStation.district.value,
          };

          this.editStation(station);
        },
        () => {}
      );
    };

    this.updateRouteInfo = () => {
      const updateRoute = this.$mdDialog.show({
        controller: routeInfoDialogCtrl,
        controllerAs: "$ctrl",
        template: routeInfoDialog,
        multiple: true,
        locals: {
          route: this.route,
        },
      });

      updateRoute.then(
        (updatedRoute) => {
          this.route = {
            ...this.route,
            name: updatedRoute.name.value,
            nameAr: updatedRoute.nameAr.value,
            name_i18n: {
              en: updatedRoute.name.value,
              ar: updatedRoute.nameAr.value,
            },
            description: updatedRoute.description.value,
            price: updatedRoute.price.value,
          };
        },
        () => {}
      );
    };

    this.updateMapStations = (stations) => {
      this.route.stations = stations;
      this.stations = map(formatStations)(stations);
    };

    this.getSatisfiedDemand = () => {
      this.isFetchingDemand = true;
      const { origin, destination, time, type } = this.route;
      const points = mapStations(this.route.stations);
      const item = {
        polygon1: findId(origin) || this.params.polygon1,
        polygon2: findId(destination) || this.params.polygon2,
        points,
        time: time || this.params.time,
        type: type || this.params.type,
      };

      this.Rougena.getSatisfiedDemand(item).$promise.then(
        (response) => {
          updateDemand(response);
          this.isFetchingDemand = false;
        },
        () => {
          this.isFetchingDemand = false;
        }
      );
    };

    this.saveRoute = () => {
      const {
        origin,
        destination,
        name,
        nameAr,
        price,
        description,
        city,
        time,
        type,
      } = this.route;
      const route = mapStations(this.route.stations);
      const item = {
        name_i18n: {
          en: name,
          ar: nameAr,
        },
        price,
        description,
        city: findId(city),
        polygon1: findId(origin) || this.params.polygon1,
        polygon2: findId(destination) || this.params.polygon2,
        route,
        time: time || this.params.time,
        type: type || this.params.type,
      };

      this.Rougena.saveRoute(item).$promise.then(
        (response) => {
          this.route._id = response.routeId;
        },
        () => {}
      );
    };

    this.publishRoute = () => {
      const item = {
        routeId: this.route._id,
      };

      this.Rougena.publishRoute(item).$promise.then(
        (response) => {
          this.$state.go("route", { id: response.routeId });
        },
        (response) => this.Events.emit(errorEvent, response.data)
      );
    };

    this.Events.on(this.updateStationsEvent, this.updateMapStations);
    this.Events.on(this.updateDemandEvent, this.getSatisfiedDemand);
    this.Events.on(this.editStationEvent, this.updateStationInfo);
    this.Events.on(this.toggleOptimizationEvent, this.toggleOptimization);
    this.Events.on(this.toggleCreateModeEvent, this.toggleCreateMode);
  }
}

export default Controller;



// WEBPACK FOOTER //
// ./src/pages/routeEdit/routeEdit.controller.js